import React from 'react';
import PropTypes from 'prop-types';
import useAddressAutoCompleteContext, { AddressAutoCompleteContextProvider } from '../../../../context/addressAutoCompleteContext';
import { useTranslation } from 'react-i18next';
import { classnames } from '../../../../utils/classnames';
import { autocompleteDisabled } from '../../../../utils/utils';
import FooterDropdown from './GoogleAddress';
import { Loader } from 'semantic-ui-react';
import InputErrors, { withErrors } from '../inputErrors/inputErrors';
import Icon from '../icon/icon';
import './addressAutocomplete.css';
import '../inputs/inputs.css';

// autocomplete field and select a prediction

function Address({ address, onClick, preSelected=false }) {
	return (
		<div
			tabIndex="-1"
			key={address.id}
			onMouseDown={() => onClick(address)}
			onClick={() => onClick(address)}
			className={classnames("address-element", (preSelected && 'selected'))}
		>
			<strong>{address.label}</strong>
		</div>
	)
}

// suggested addresses by api-addresses
function AddressSuggestions({ api, addresses = [], setAddressAuto, onAddressSelected = () => { }, displayed = true, selectedAddressIndex=null }) {
	return (
		<div className="addresses-suggestions">
			<div className="addresses-suggestions-container">
				{displayed &&
					addresses.map((address, i) =>
						<Address
							key={address.id}
							address={address}
							onClick={onAddressSelected}
							preSelected={selectedAddressIndex === i}
						/>
					)
				}
				{api === 'google' && <FooterDropdown key="footer" onAutoModeChange={setAddressAuto} />}
			</div>
		</div>
	);
}

function AddressSearchInput({
	id,
	apiParams,
	debounceTimeout = 350,
	className = null,
	filters = {},
	onChange = () => {},
	handleAddress = () => {},
	hideAllRelated = () => {},
	isAddressAuto = false,
	changeAutoMode,
	required = false,
	disabled = false,
	formKeys,
	formObject,
	setFormObject = () => {},
	fieldId,
	errors,
	ariaControls,
	api = 'default',
	fieldSelectorMapping,
	...props
}) {
	const { t } = useTranslation();
	const [inputFocused, setInputFocused] = React.useState(false);
	const aggregatedAddress = React.useMemo(() => ['addresses', 'postalCodes', 'cities', 'countries']
		.map((key) => formObject[fieldSelectorMapping[key]])
		.filter(f => f)
		.join(' '),
		[formObject, fieldSelectorMapping]
	);
	const [inputValue, setInputValue] = React.useState(aggregatedAddress);
	const [previousFields, setPreviousFields] = React.useState(aggregatedAddress);
	const [selectedAddress, setSelectedAddress] = React.useState(null);
	const refAutocomplete = React.useRef(null);

	// when address is selected we update the fields
	const updateAddressToFields = React.useCallback((address) => {
		setInputFocused(false);
		if (address) {
			const aggregatedAddress = ['addresses', 'postalCodes', 'cities', 'countries'].map(key => fieldGetDataMapping[key](address)).join(' ');
			setPreviousFields(aggregatedAddress);
			onChange(address.raw);
			setInputValue(address.raw);
			onFocusOrSelect();
		}
		handleAddress(address);
	}, [onChange, handleAddress]);

	const {search, get_details, addresses, place, loading} = useAddressAutoCompleteContext({ onPlaceDetails: updateAddressToFields });

	// fill autoComplete input field with new address if one of the address fields is updated
	React.useEffect(() => {
		let resetCoordinates = {};
		if (formKeys['coordinates']?.length)
			resetCoordinates = {[formKeys['coordinates'][0]]: ''}
		// base comparison on previousFields to allow inputValue to be different
		if (!isAddressAuto && (aggregatedAddress !== previousFields || (!formObject[fieldId] && aggregatedAddress))) {
			setPreviousFields(aggregatedAddress);
			setFormObject({ ...resetCoordinates, [fieldId]: aggregatedAddress });
			setInputValue(aggregatedAddress);
		}
	}, [isAddressAuto, inputValue, formObject, fieldId, formKeys, previousFields, aggregatedAddress])

	const addressesOpen = React.useMemo(() =>
		inputFocused && addresses && addresses?.length > 1 && inputValue !== formObject[fieldId],
		[addresses, inputValue, place, inputFocused, addresses, formObject, fieldId]
	);

	const onFocusOrSelect = React.useCallback((focus = false) => {
		if (focus) setInputFocused(focus);
		setTimeout(() => {
			search(inputValue);
		}, debounceTimeout);
	}, [search, inputValue, debounceTimeout]);

	// change pre-selected suggestion
	const walkWithKeys = React.useCallback(e => {
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			setSelectedAddress(Math.min((selectedAddress !== null ? selectedAddress + 1 : 0), addresses?.length - 1));
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			setSelectedAddress(Math.max((selectedAddress !== null ? selectedAddress - 1 : 0), 0));
		}
		else if (e.key === 'Enter' && !isNaN(selectedAddress) && selectedAddress !== null) {
			get_details(addresses[selectedAddress].id);
		}
	}, [selectedAddress, addresses, setSelectedAddress, get_details]);

	// we open the "dropdown" on different conditions depending on the api
	const highlightInput = React.useMemo(() => {
		switch (api) {
			case 'google': return inputFocused;
			default: return addressesOpen;
		}
	}, [inputFocused, addressesOpen, api]);

	// props of the autocomplete input
	const propsInput = React.useMemo(() => {return {
			fieldId: fieldId,
			autoComplete: autocompleteDisabled(),
			value: inputValue,
			className: classnames(className, 'address-autocomplete-input', (highlightInput && 'highlight'), 'neo-input', (withErrors(errors) && 'error')),
			disabled: disabled,
			placeholder: t('widget.googleAddress.placeholder'),
			type: "text",
			onChange: e => {
				let value = e.target.value;
				setInputFocused(true);
				setInputValue(value)
				setSelectedAddress(null);
				if (isAddressAuto) {
					setTimeout(() => {
						search(value);
					}, debounceTimeout);
				}
			},
			onKeyDown: walkWithKeys,
			onFocus: () => onFocusOrSelect(true),
			onClick: () => onFocusOrSelect(true),
			onBlur: () => {
				//Timeout because the blur event is triggered before the state is set
				setTimeout(() => {
					setSelectedAddress(null);
				}, 0);
			},
			required: required,
			'aria-describedby': props['aria-describedby']
		}}, [id, inputValue, clearTimeout, search, walkWithKeys, t, addressesOpen, errors, required, disabled, isAddressAuto, props]);
	
	return (
		<>
			{isAddressAuto && (
				<div
					ref={refAutocomplete}
					className={"neo-autocomplete"}
					onFocus={() => {onFocusOrSelect(true)}}
					// if address suggestion we don't want to hide the suggestions otherwise we cant click on them (onblur is played before onclick)
					onBlur={(e) => {if (!e?.relatedTarget?.className?.includes('address-element')) setTimeout(() => setInputFocused(false), 50)}}
				>
					{loading && <Loader className="neo-loader" active size="small" />}
					<input {...propsInput} />
					{isAddressAuto && <InputErrors errors={errors} id={`${id}_error`} />}
					{highlightInput && <AddressSuggestions
						api={api}
						addresses={addresses}
						setAddressAuto={changeAutoMode}
						onAddressSelected={address => {
							get_details(address.id);
						}}
						walkWithKeys={walkWithKeys}
						selectedAddressIndex={selectedAddress}
						displayed={addressesOpen}
            		/>}
				</div>
			)}
		</>
	)
}

AddressSearchInput.propTypes = {
    onlyAddress: PropTypes.bool,
    formObject: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    onAutoModeChange: PropTypes.func,
    onError: PropTypes.func,
};

AddressSearchInput.defaultProps = {
    onlyAddress: false
};

// choose between autocomplete and manual mode
export function AddressAutocompleteContainer({
    className = null,
	label = null,
	description = null,
	fieldId = null,
	uniqueKey = null,
	handleAddress,
	hideAllRelated,
	idsControlled,
	required = true,
	display = true,
	formObject,
	...props
}) {
	const {loadingApi, available} = useAddressAutoCompleteContext({});
	const [initiating, setInitiating] = React.useState(true);

	const { t } = useTranslation();
	const [isAddressAuto, setAddressAuto] = React.useState(true);
	const refAutocomplete = React.useRef(null);
	const getKey = (element = null) => `field_${uniqueKey}${element ? '_' + element : ''}`;

	const refreshHideFields = React.useCallback(() => { 
		hideAllRelated(isAddressAuto)
	}, [isAddressAuto, hideAllRelated]);

	React.useEffect(() => {
		// on display change (due to conditional_display for ex) we reset fields display based on isAddressAuto
		if (display) {
			refreshHideFields();
		}
	}, [display]);

	// if search api not available we prevent addressauto activation and default on fields
	React.useEffect(() => {
		if (initiating) {
			// hide cause error or not loaded yet
			if (!loadingApi && !available && isAddressAuto) {
				setInitiating(false);
				setAddressAuto(false);
				hideAllRelated(false);
			}
			// show cause loaded and wasn't visible
			if (available && !isAddressAuto) {
				setInitiating(false);
				setAddressAuto(true);
				hideAllRelated(true);
			}
			if (!loadingApi && available) {
				setInitiating(false);
			}
		}
	}, [loadingApi, available, isAddressAuto, initiating]);

	const changeAutoMode = React.useCallback(() => {
		setAddressAuto(!isAddressAuto);
		hideAllRelated(!isAddressAuto);
		if (!isAddressAuto)
			refAutocomplete?.current?.ariaFocus();
	}, [isAddressAuto, setAddressAuto, hideAllRelated, refAutocomplete])

	if (!display || !available) {
		return null;
	}

	return (
		<div className="neo-address-autocomplete-container">
			<div id={'attribution'}></div>
	        <div className={classnames('neo-input-content', className, (isAddressAuto && 'marginbottom'))}>
				{isAddressAuto && label && <label className='neo-input-label' for={getKey()}>{label} {required && <span aria-label={t('widget.required-field')} className='neo-input-required'>*</span>}</label>}
            	{isAddressAuto && description && <label className='neo-input-description' id={getKey('description')}>{description}</label>}
				<AddressSearchInput
					id={getKey()}
					isAddressAuto={isAddressAuto}
					changeAutoMode={changeAutoMode}
					ref={refAutocomplete}
					aria-describedby={`${getKey('description')} ${getKey('error')}`}
					ariaControls={idsControlled}
					icon={'mapPin'}
					fieldId={fieldId}
					formObject={formObject}
					handleAddress={handleAddress}
					onError={(e) => console.warn(e)}
					{...props}
				/>
				<button
					id={`btn-switch_${fieldId}`}
					className='neo-autocomplete-switch'
					tabIndex={0}
					aria-controls={getKey()}
					aria-expanded={!isAddressAuto}
					onClick={() => changeAutoMode()}
				>
					<Icon name={isAddressAuto ? 'building': 'mapPin'} />
					<span for={`btn-switch_${fieldId}`}>
						{isAddressAuto ? t('widget.input.address.disable') : t('widget.input.address.activate')}
					</span>
				</button>
			</div>
		</div>
	);
}

const addressAutoCompleteMapping = {
	'default': {
	  'addresses': 'ws-8',
	  'cities': 'ws-28',
	  'coordinates': 'latitude_longitude_',
	  'countries': 'ws-29',
	  'postalCodes': 'ws-27',
	},
	'company': {
	  'addresses': 'ws-11',
	  'cities': 'ws-31',
	  'coordinates': 'latitude_longitude_',
	  'countries': 'ws-32',
	  'postalCodes': 'ws-30',
	},
	'delivery': {
	  'addresses': 'ws-18',
	  'cities': 'ws-34',
	  'coordinates': 'latitude_longitude_',
	  'countries': 'ws-35',
	  'postalCodes': 'ws-33',
	},
}

const usesCityAndStates = ['US'];

const fieldGetDataMapping = {
	'postalCodes': (a) => a.address.zip_code,
	// use city or state (for KR, TW, ID, TR, ...) and both for US
	'cities': (a) => [a.address.city, !a.address.city || usesCityAndStates.includes(a.address.country) ? a.address.state : null].filter(v => v).join(', '),
	'countries': (a) => a.address.country,
	'coordinates': (a) => a.address.coordinates,
	'addresses': (a) => a.address.address1 || a.address.name || a.full_address
};

// form and fields controller
export default function AddressAutoComplete({ fieldId, uniqueKey, formObject, setFormObject, addressType = 'default', ...props }) {
	const [fields, setFields] = React.useState({});
	const [formKeys, setFormKeys] =  React.useState({});

	const formId = React.useMemo(() => fieldId && uniqueKey ? uniqueKey?.slice(0, uniqueKey?.lastIndexOf(`_${fieldId}`)) : '', [fieldId, uniqueKey]);

	const fieldSelectorMapping = React.useMemo(() => addressAutoCompleteMapping[addressType],[addressType]);

	// Handle Related inputs
	const hideAllRelated = React.useCallback((hidden) => {
		Object.keys(fieldSelectorMapping).forEach(key => {
			fields[key]?.forEach(element => {
				if (hidden)
					element.parentElement.parentElement.setAttribute('hidden', true);
				else if (key !== 'coordinates')
					element.parentElement.parentElement.removeAttribute('hidden');
			});
		})
	}, [fields, fieldSelectorMapping]);

	// Init the fields / get elements with selector
	const initFields = React.useCallback(() => {
		const newFields = {};
		const newFormKeys = {};
		if (!(formId && !Object.keys(fields)?.length && !Object.keys(formKeys)?.length)) return;
		Object.keys(fieldSelectorMapping).forEach(key => {
			// try country-dropdown first
			let allElements = Array.from(document.querySelectorAll(`.neo-country .neo-dropdown-content [id^=field_${formId}_${fieldSelectorMapping[key]}]`));
			if (!allElements?.length)
				allElements = Array.from(document.querySelectorAll(`input[id^=field_${formId}_${fieldSelectorMapping[key]}]`));
			newFields[key] = allElements;
			newFormKeys[key] = allElements.map(element => element.id.replace(`field_${formId}_`, '').replace('latitude_longitude_', ''));
		});
		setFields(newFields);
		setFormKeys(newFormKeys);
	}, [formId, fields, formKeys, fieldSelectorMapping]);

	React.useEffect(() => initFields(), []);

	const handleAddress = React.useCallback((address) => {
		if (!address) return;
		// update form object
		const newObject = { ...formObject, [fieldId]: address.raw };
		Object.keys(fieldGetDataMapping)?.forEach(key => {
			const value = fieldGetDataMapping[key](address);
			const stringValue = typeof value !== 'string' ? JSON.stringify(value)?.replace(new RegExp(/[\][]/gm), '') : value;
			formKeys[key]?.forEach(keyForm => {
				newObject[keyForm] = stringValue;
			});
		});
		setFormObject(newObject);
	}, [fieldId, formObject, fields, formKeys, setFormObject]);

	const idsControlled = React.useMemo(() => Object.values(fields)
		?.map(f => f?.length ? f[0].id : f)
		.filter(e => !e.includes('latitude_longitude'))
		?.join(' '), [fields]);

	const _props = {
		fieldId,
		formObject,
		formKeys,
		setFormObject,
		handleAddress,
		hideAllRelated,
		idsControlled,
		fieldSelectorMapping,
		...props
	};

	return (
		<AddressAutoCompleteContextProvider api={props.api} >
			<AddressAutocompleteContainer {..._props} />
		</AddressAutoCompleteContextProvider>
	)
}
